// An answer row in a quiz or a drill: the whole row is the target, a number in a circle leads, a
// mark (right, wrong) may follow. Its state is a colour and a border, solid in the accessible
// version, and the caller adds the words that say it.
import "./config";
import { Text, View, styled } from "tamagui";
import { Pressable } from "./controls";
import { Fill } from "./layout";
import { Body } from "./text";

const OptionFrame = styled(Pressable, {
  name: "Option",
  flexDirection: "row",
  justifyContent: "flex-start",
  gap: "3",
  width: "100%",
  minHeight: { default: 56, a11yLight: 64, a11yDark: 64 },
  paddingVertical: "3",
  paddingHorizontal: "3",
  borderRadius: "3",
  borderWidth: "var(--borderW)",
  borderColor: "line",
  backgroundColor: { default: "surface", press: "surface2" },
  textAlign: "left",
  variants: {
    state: {
      selected: { borderColor: "accent", backgroundColor: "accentSoft" },
      correct: {
        borderColor: "ready",
        backgroundColor: { default: "readyBg", a11yLight: "ready", a11yDark: "ready" },
      },
      incorrect: {
        borderColor: "wait",
        backgroundColor: { default: "waitBg", a11yLight: "wait", a11yDark: "wait" },
      },
      /** The answer that was right, shown after a wrong pick. */
      right: { borderColor: "ready", borderWidth: 3 },
    },
  } as const,
});

type OptionState = "selected" | "correct" | "incorrect" | "right";

const INK = {
  correct: { default: "ready", a11yLight: "bg", a11yDark: "bg" },
  incorrect: { default: "wait", a11yLight: "bg", a11yDark: "bg" },
} as const;

export interface OptionProps extends Omit<
  React.ComponentProps<typeof OptionFrame>,
  "state" | "label"
> {
  label: string;
  /** The number in the circle before the label (1-based); leave out for a plain row. */
  number?: number;
  state?: OptionState;
  /** What follows the label: a mark and a word ("Goed"). */
  mark?: React.ReactNode;
}

export function Option({ label, number, state, mark, ...rest }: OptionProps): React.JSX.Element {
  const ink = state === "correct" || state === "incorrect" ? INK[state] : "text";
  return (
    // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
    <OptionFrame {...(state === undefined ? {} : { state })} {...rest}>
      {number === undefined ? null : (
        <View
          width={28}
          height={28}
          flexShrink={0}
          borderRadius="pill"
          backgroundColor="surface3"
          alignItems="center"
          justifyContent="center"
        >
          <Text fontFamily="body" fontSize="3" fontWeight="700" color="textDim">
            {number}
          </Text>
        </View>
      )}
      <Fill>
        <Body weight="medium" color={ink}>
          {label}
        </Body>
      </Fill>
      {mark}
    </OptionFrame>
  );
}
